import { GridState, StateUpdatedEvent } from 'ag-grid-community';
import { setGridSettings, useAgLocalStorage } from './useAgLocalStorage';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useGridButtons } from './useGridButtons';
import debounce from 'lodash/debounce';

interface UseAgGridCommonParams {
  gridName: string;
  gridRef?: React.RefObject<AgGridReact> | undefined;
  version?: number;
}

const ON_STATE_UPDATE_DEBOUNCE_MS = 300;

export const useAgGridCommon = ({
  gridName,
  gridRef = undefined,
  version,
}: UseAgGridCommonParams) => {
  const [gridVisible, setGridVisible] = useState(true);
  const initialGridSettings: GridState = useAgLocalStorage(gridName, version);
  const gridButtons = useGridButtons(gridRef);

  // Used to force a re-render if initialGridSettings has changed, b/c AgGridReact
  // initialState prop is only read on initialization
  const reloadGrid = useCallback(() => {
    setGridVisible(false);
    setTimeout(() => {
      setGridVisible(true);
    });
  }, []);

  useEffect(() => {
    reloadGrid();
  }, [initialGridSettings, reloadGrid]);

  const _onStateUpdated = useCallback(
    (event: StateUpdatedEvent) => {
      setGridSettings(gridName, event.state, version);
    },
    [gridName, version],
  );

  // Memoized debounce callback so its not recreated
  const debouncedOnStateUpdated = useMemo(
    () => debounce(_onStateUpdated, ON_STATE_UPDATE_DEBOUNCE_MS),
    [_onStateUpdated],
  );

  return {
    gridVisible: gridVisible,
    initialGridSettings,
    onStateUpdated: debouncedOnStateUpdated,
    gridButtons,
  };
};
